import React from 'react';
import Helmet from 'react-helmet';
// eslint-disable-next-line import/no-unresolved
import { useLocation } from '@reach/router';
import { css } from '@emotion/react';
import DesktopHeader from './desktop-header';
import TopNav from './top-nav';
import { colors } from '../primitives/colors';
import MobileHeader from './mobile-header';
import { breakpoints } from '../primitives/tokens';
import { useTemplateProvider } from '../../contexts/template-provider';
import { PageTemplate } from '../../utils/match-page-template';
import { useCtaProvider } from '../../contexts/franchise/cta-provider';
import { useMicrosite } from '../../contexts/franchise/microsite-context';

const styles = {
    topNavContainer: css({
        zIndex: 100,
        backgroundColor: colors.shadesWhite,
        position: 'relative',
        display: 'none',
        '.top-bar &': {
            display: 'block',
            [breakpoints.mb]: {
                display: 'none',
            },
        },
        [breakpoints.mb]: {
            display: 'none',
        },
    }),
    groupedHeaderContainer: css({
        position: 'sticky',
        top: '0', // Fix subpixel antialias on some monitors causing bad sticky
        zIndex: 100,
        backgroundColor: colors.shadesWhite,
        borderBottom: `1px solid ${colors.grey100}`,
        transition: 'box-shadow 0.2s',
        '.scrolled &': {
            boxShadow: '0 -2px 12px 0 #0001',
        },
    }),
    desktopHeader: css({
        [breakpoints.mb]: {
            display: 'none',
        },
    }),
    mobileHeader: css({
        display: 'none',
        [breakpoints.mb]: {
            display: 'block',
        },
    }),
};
const Header = (): JSX.Element => {
    const prevScrollRef = React.useRef<number>(0);

    const { template } = useTemplateProvider();
    const location = useLocation();
    const { isMicrosite } = useMicrosite();

    const {
        hideFranchiseLink,
    } = useCtaProvider();

    const noneConditions = [
        template === PageTemplate.MicrositePage,
        template === PageTemplate.MicrositeService,
        template === PageTemplate.LocationsIndex,
        template === PageTemplate.HyperLocal,
        template === PageTemplate.MetroState,
        hideFranchiseLink,
        location.pathname.endsWith('/advertising/pro-for-that'),
    ];

    React.useEffect(() => {
        const handleScroll = (): void => {
            if (window.scrollY > 40) {
                document.body.classList.add('scrolled');
            } else {
                document.body.classList.remove('scrolled');
            }
            if (window.scrollY < prevScrollRef.current) {
                document.body.classList.add('scroll-up');
            } else {
                document.body.classList.remove('scroll-up');
            }

            prevScrollRef.current = window.scrollY;
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const classes = [
        template,
        noneConditions.some(x => x) ? '' : 'top-bar',
        isMicrosite ? 'is-microsite' : 'is-std',
    ].filter(x => !!x).join(' ');

    return (
        <>
            {/* Helmet doesn't SSR these correctly without both attribs */}
            <Helmet htmlAttributes={{ className: classes, class: classes }} />
            <div css={styles.topNavContainer}>
                <TopNav />
            </div>
            <div css={styles.groupedHeaderContainer}>
                <div css={styles.desktopHeader}>
                    <DesktopHeader />
                </div>
                <div css={styles.mobileHeader}>
                    <MobileHeader />
                </div>
            </div>
        </>
    );
};

export default Header;
